<template>
  <q-page class="ace-page">
    <!-- Loading -->
    <template v-if="adminStore.editLoading">
      <div class="ace-loading">
        <q-skeleton type="rect" height="40px" style="margin-bottom: 16px;" />
        <div class="ace-split">
          <div class="ace-left-panel">
            <q-skeleton v-for="n in 3" :key="n" type="text" height="40px" style="margin-bottom: 8px;" />
          </div>
          <div class="ace-right-panel">
            <q-skeleton type="rect" height="360px" />
          </div>
        </div>
      </div>
    </template>

    <!-- Error -->
    <div v-else-if="adminStore.editError" class="ace-center-msg">
      <p style="color: var(--oa-danger); margin-bottom: 8px;">{{ adminStore.editError }}</p>
      <q-btn flat no-caps label="Reintentar" color="primary" @click="loadCourse" />
    </div>

    <!-- Content -->
    <template v-else-if="course">
      <!-- Header -->
      <div class="ace-header">
        <div class="ace-breadcrumb">
          <router-link to="/admin" class="ace-crumb">Admin</router-link>
          <span class="ace-crumb-sep">/</span>
          <router-link to="/admin/courses" class="ace-crumb">Cursos</router-link>
          <span class="ace-crumb-sep">/</span>
          <span class="ace-crumb ace-crumb--active">{{ course.title }}</span>
        </div>
        <div class="ace-header-row">
          <h1 class="ace-title">{{ course.title }}</h1>
          <q-badge
            :label="course.status === 'published' ? 'Publicado' : 'Borrador'"
            :style="{
              background: course.status === 'published'
                ? 'color-mix(in srgb, var(--color-teal-wave, #1b8fa0) 15%, transparent)'
                : 'color-mix(in srgb, var(--color-sun-gold, #e8a020) 15%, transparent)',
              color: course.status === 'published'
                ? 'var(--color-teal-wave, #1b8fa0)'
                : 'var(--color-sun-gold, #e8a020)',
              fontSize: '12px',
              padding: '3px 10px',
              borderRadius: '8px',
            }"
          />
        </div>
      </div>

      <!-- Split layout -->
      <div class="ace-split">
        <!-- Left panel: unit/lesson tree -->
        <div class="ace-left-panel">
          <!-- Desktop: plain panel label + tree -->
          <template v-if="!isMobile">
            <p class="ace-panel-label">Estructura del curso</p>
            <UnitTree
              :course-id="(route.params.id as string)"
              :units="course.units ?? []"
              :active-lesson-id="activeLessonId"
              @open-lesson="handleOpenLesson"
            />
          </template>

          <!-- Mobile: collapsed q-expansion-item -->
          <q-expansion-item
            v-else
            label="Estructura del curso"
            header-class="ace-expansion-header"
            expand-icon-class="text-primary"
            default-opened
          >
            <UnitTree
              :course-id="(route.params.id as string)"
              :units="course.units ?? []"
              :active-lesson-id="activeLessonId"
              @open-lesson="handleOpenLesson"
            />
          </q-expansion-item>
        </div>

        <!-- Right panel: course metadata form -->
        <div class="ace-right-panel">
          <q-form class="ace-form" @submit.prevent="handleUpdate">
            <!-- Titulo -->
            <q-input
              v-model="editForm.title"
              outlined
              label="Título"
              :rules="[(v: string) => !!v || 'El título es obligatorio']"
              maxlength="255"
              class="ace-field"
            />

            <!-- Slug -->
            <q-input
              v-model="editForm.slug"
              outlined
              label="Slug"
              hint="Identificador único en la URL"
              :rules="[
                (v: string) => !!v || 'El slug es obligatorio',
                (v: string) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(v) || 'Solo minúsculas, números y guiones',
              ]"
              maxlength="255"
              class="ace-field"
            />

            <!-- Descripcion corta -->
            <q-input
              v-model="editForm.shortDescription"
              outlined
              label="Descripción corta"
              type="textarea"
              :rows="2"
              maxlength="500"
              class="ace-field"
            />

            <!-- Descripcion completa -->
            <q-input
              v-model="editForm.description"
              outlined
              label="Descripción completa"
              type="textarea"
              :rows="4"
              class="ace-field"
            />

            <!-- Estado -->
            <div class="ace-field">
              <p class="ace-label">Estado</p>
              <q-btn-toggle
                v-model="editForm.status"
                no-caps
                unelevated
                :options="[
                  { label: 'Borrador', value: 'draft' },
                  { label: 'Publicado', value: 'published' },
                ]"
                :toggle-color="editForm.status === 'published' ? 'positive' : 'warning'"
                color="grey-3"
                text-color="grey-8"
              />
            </div>

            <!-- URL de imagen de portada -->
            <q-input
              v-model="editForm.coverImageUrl"
              outlined
              label="URL de imagen de portada"
              class="ace-field"
            />

            <!-- Cover preview -->
            <div v-if="editForm.coverImageUrl" class="ace-cover-preview">
              <img :src="editForm.coverImageUrl" alt="Preview de portada" class="ace-cover-img" />
            </div>

            <!-- Actions -->
            <div class="ace-actions">
              <q-btn
                flat
                no-caps
                label="Cancelar"
                color="grey-7"
                to="/admin/courses"
              />
              <q-btn
                unelevated
                no-caps
                type="submit"
                color="accent"
                icon="save"
                label="Guardar curso"
                :loading="saving"
              />
            </div>
          </q-form>
        </div>
      </div>
    </template>
  </q-page>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { useAdminCoursesStore, type AdminLesson } from 'stores/useAdminCoursesStore';
import UnitTree from 'components/admin/UnitTree.vue';

const route = useRoute();
const router = useRouter();
const $q = useQuasar();
const adminStore = useAdminCoursesStore();

// ---------- State ----------
const saving = ref(false);
const activeLessonId = ref<string | undefined>(undefined);
const isMobile = computed(() => $q.screen.width < 768);

function handleOpenLesson(lesson: AdminLesson) {
  activeLessonId.value = lesson.id;
}

const editForm = ref({
  title: '',
  slug: '',
  shortDescription: '',
  description: '',
  coverImageUrl: '',
  status: 'draft' as 'draft' | 'published',
});

const course = ref(adminStore.editingCourse);

// ---------- Load ----------
async function loadCourse() {
  const courseId = route.params.id as string;
  await adminStore.fetchCourse(courseId);
  course.value = adminStore.editingCourse;
  if (adminStore.editingCourse) {
    populateForm(adminStore.editingCourse);
  }
}

function populateForm(c: NonNullable<typeof adminStore.editingCourse>) {
  editForm.value = {
    title: c.title,
    slug: c.slug,
    shortDescription: c.shortDescription ?? '',
    description: c.description ?? '',
    coverImageUrl: c.coverImageUrl ?? '',
    status: (c.status === 'published' ? 'published' : 'draft') as 'draft' | 'published',
  };
}

// Sync when store updates
watch(
  () => adminStore.editingCourse,
  (val) => {
    course.value = val;
  },
);

// ---------- Submit ----------
async function handleUpdate() {
  const courseId = route.params.id as string;
  saving.value = true;
  try {
    const body = {
      title: editForm.value.title,
      slug: editForm.value.slug,
      ...(editForm.value.shortDescription ? { shortDescription: editForm.value.shortDescription } : {}),
      ...(editForm.value.description ? { description: editForm.value.description } : {}),
      ...(editForm.value.coverImageUrl ? { coverImageUrl: editForm.value.coverImageUrl } : {}),
      status: editForm.value.status,
    };

    await adminStore.updateCourse(courseId, body);
    $q.notify({ type: 'positive', message: 'Curso guardado' });
  } catch (err: unknown) {
    // Revert form to last persisted state to avoid re-submitting stale/conflicting values
    if (adminStore.editingCourse) populateForm(adminStore.editingCourse);
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 409) {
      $q.notify({ type: 'negative', message: 'El slug ya está en uso' });
    } else if (status === 404) {
      $q.notify({ type: 'negative', message: 'Curso no encontrado' });
      await router.push({ name: 'admin-courses' });
    } else {
      $q.notify({ type: 'negative', message: 'No se pudo guardar. Revisá tu conexión e intentalo de nuevo.' });
    }
  } finally {
    saving.value = false;
  }
}

// ---------- Lifecycle ----------
onMounted(() => {
  loadCourse();
});
</script>

<style scoped>
.ace-page {
  background: var(--color-seafoam, #e8f4f8);
  min-height: 100vh;
}

.ace-loading,
.ace-center-msg {
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
}

.ace-center-msg {
  text-align: center;
  padding-top: 48px;
}

/* Header */
.ace-header {
  padding: 16px 24px 0;
  max-width: 1100px;
  margin: 0 auto;
}

.ace-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--oa-text-muted);
  margin-bottom: 8px;
}

.ace-crumb {
  color: var(--oa-text-muted);
  text-decoration: none;
  transition: color 0.15s;
}

.ace-crumb:hover {
  color: var(--oa-text-primary);
}

.ace-crumb--active {
  color: var(--oa-text-primary);
}

.ace-crumb-sep {
  color: var(--oa-text-disabled);
}

.ace-header-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.ace-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

/* Split layout */
.ace-split {
  display: flex;
  align-items: flex-start;
  max-width: 1100px;
  margin: 0 auto;
  min-height: calc(100vh - 120px);
}

/* Left panel */
.ace-left-panel {
  width: 340px;
  flex-shrink: 0;
  background: var(--color-sand-light, #f5efe6);
  border-right: 1px solid var(--oa-border);
  min-height: calc(100vh - 120px);
  padding: 16px;
}

.ace-panel-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--oa-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 16px;
}

.ace-tree-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  background: var(--oa-bg-3);
  border: 1px dashed var(--oa-border-strong);
  border-radius: 8px;
  text-align: center;
}

/* Right panel */
.ace-right-panel {
  flex: 1;
  min-width: 0;
  background: var(--color-seafoam, #e8f4f8);
  padding: 24px;
}

/* Form */
.ace-form {
  background: var(--color-sand-light, #f5efe6);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 720px;
}

.ace-field {
  margin-bottom: 8px;
}

.ace-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--oa-text-secondary);
  margin: 0 0 8px;
}

.ace-cover-preview {
  margin-bottom: 8px;
  border-radius: 8px;
  overflow: hidden;
  max-height: 200px;
}

.ace-cover-img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.ace-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--oa-border);
}

/* Mobile: stack panels */
@media (max-width: 767px) {
  .ace-split {
    flex-direction: column;
  }

  .ace-left-panel {
    width: 100%;
    min-height: auto;
    border-right: none;
    border-bottom: 1px solid var(--oa-border);
  }
}
</style>
